import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { PARSE_DATE_PROPS } from 'src/app/shared/interceptors/date-interceptor'
import { GLOBAL_LOADING_SPINNER_ENABLED } from 'src/app/shared/interceptors/loading-spinner-interceptor'
import { APIEnvelope } from 'src/app/shared/models/api'
import { PaginationParams, FilterParams, SortParams, httpParams, withHttpParams, pagination, sort, filter } from 'src/app/shared/utilities/http-params'
import {
  TBillCACPHourOverrideRow,
  TBillJobStatus,
  TBillNITSRate,
  TBillNITSRateCreateRequest,
  TBillNITSRateUpdateRequest,
  TBillRetailerUsageOverride,
  TBillRetailerUsageOverrideCreateRequest,
  TBillRetailerUsageOverrideUpdateRequest,
  TBillSchedule1Rate,
  TBillSchedule1RateCreateRequest,
  TBillSchedule1RateUpdateRequest,
  TBillSPCharge,
  TBillSPChargeCreateRequest,
  TBillSPChargeUpdateRequest, TBillCACPHourOverride,
} from './tbill-settlements.types'
import { formatAsUTC } from 'src/app/shared/utilities/date'
import { PARSE_NUMBER_PROPS } from 'src/app/shared/interceptors/number-interceptor'

@Injectable({ providedIn: 'root' })
export class TBillSettlementsService {
  http = inject(HttpClient)

  runTBill(
    billingMonth: Date, // yyyy-MM-01
    settlementType: string,
  ): Observable<unknown> {
    return this.http
      .post('tbill', {
        billing_month: formatAsUTC(billingMonth, false),
        settlement_type: settlementType,
      })
  }

  getTBillJobs(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillJobStatus>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillJobStatus>>(
      'tbill/job_status',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['billing_month', 'operating_day', 'start_time', 'end_time', 'create_time']),
      },
    )
  }

  getTBillRetailerUsageOverrides(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillRetailerUsageOverride>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillRetailerUsageOverride>>(
      'tbill/retailer_usage_overrides',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['market_interval', 'create_time', 'last_update_time'])
          .set(PARSE_NUMBER_PROPS, ['measurement_value']),
      },
    )
  }

  createTBillRetailerUsageOverride(req: TBillRetailerUsageOverrideCreateRequest): Observable<unknown> {
    return this.http.post('tbill/retailer_usage_overrides', req)
  }

  updateTBillRetailerUsageOverride(
    retailer_uid: string, 
    market_interval: Date, 
    req: TBillRetailerUsageOverrideUpdateRequest,
  ): Observable<unknown> {
    return this.http.put(
      `tbill/retailer_usage_overrides/${retailer_uid}/${formatAsUTC(market_interval)}`,
      req,
    )
  }

  deleteTBillRetailerUsageOverride(retailer_uid: string, market_interval: Date): Observable<unknown> {
    return this.http.delete(`tbill/retailer_usage_overrides/${retailer_uid}/${formatAsUTC(market_interval)}`)
  }

  getTBillCACPHourOverrides(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillCACPHourOverrideRow>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillCACPHourOverrideRow>>(
      'tbill/ca_cp_hour_overrides',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['month', 'hour', 'create_time', 'last_update_time']),
      },
    )
  }

  createTBillCACPHourOverride(override: TBillCACPHourOverride): Observable<unknown> {
    return this.http.post('tbill/ca_cp_hour_overrides', override)
  }

  updateTBillCACPHourOverride(control_area_code: string, month: Date, hour: Date): Observable<unknown> {
    return this.http.put( `tbill/ca_cp_hour_overrides/${control_area_code}/${formatAsUTC(month)}`, { hour })
  }

  deleteTBillCACPHourOverride(control_area_uid: string, month: Date): Observable<unknown> {
    return this.http.delete(`tbill/ca_cp_hour_overrides/${control_area_uid}/${formatAsUTC(month)}`)
  }

  getTBillSPCharges(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillSPCharge>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillSPCharge>>(
      'tbill/retailer_sp_charges',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['effective_date', 'termination_date', 'create_time', 'last_update_time']),
      },
    )
  }

  createTBillSPCharge(req: TBillSPChargeCreateRequest): Observable<unknown> {
    return this.http.post('tbill/retailer_sp_charges', req)
  }

  updateTBillSPCharge(
    retailer_uid: string,
    tbill_sp_charge_type_uid: string,
    effective_date: Date,
    req: TBillSPChargeUpdateRequest,
  ): Observable<unknown> {
    return this.http.put(
      `tbill/retailer_sp_charges/${retailer_uid}/${tbill_sp_charge_type_uid}/${formatAsUTC(effective_date)}`,
      req,
    )
  }

  deleteTBillSPCharge(retailer_uid: string, tbill_sp_charge_type_uid: string, effective_date: Date): Observable<unknown> {
    return this.http.delete(
      `bill/retailer_sp_charges/${retailer_uid}/${tbill_sp_charge_type_uid}/${formatAsUTC(effective_date)}`,
    )
  }

  getTBillNITSRates(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillNITSRate>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillNITSRate>>(
      'tbill/nits_rate',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['month', 'create_time', 'last_update_time'])
          .set(PARSE_NUMBER_PROPS, ['rate']),
      },
    )
  }

  createTBillNITSRate(req: TBillNITSRateCreateRequest): Observable<unknown> {
    return this.http.post('tbill/nits_rate', req)
  }

  updateTBillNITSRate(control_area_uid: string, month: Date, req: TBillNITSRateUpdateRequest): Observable<unknown> {
    return this.http.put(`tbill/nits_rate/${control_area_uid}/${formatAsUTC(month)}`, req)
  }

  deleteTBillNITSRate(control_area_uid: string, month: Date): Observable<unknown> {
    return this.http.delete(`tbill/nits_rate/${control_area_uid}/${formatAsUTC(month)}`)
  }

  getTBillSchedule1Rates(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<TBillSchedule1Rate>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<TBillSchedule1Rate>>(
      'tbill/schedule_1',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['month', 'create_time', 'last_update_time'])
          .set(PARSE_NUMBER_PROPS, ['rate']),
      },
    )
  }

  createTBillSchedule1Rate(req: TBillSchedule1RateCreateRequest): Observable<unknown> {
    return this.http.post('tbill/schedule_1', req)
  }

  updateTBillSchedule1Rate(month: Date, req: TBillSchedule1RateUpdateRequest): Observable<unknown> {
    return this.http.put(    `tbill/schedule_1/${formatAsUTC(month)}`, req)
  }

  deleteTBillSchedule1Rate(month: Date): Observable<unknown> {
    return this.http.delete(`tbill/schedule_1/${formatAsUTC(month)}`)
  }
}
